import React, { useState } from 'react';
import { Play, ChevronRight, CheckCircle, Zap, TrendingUp, Shield, Code, Users, DollarSign, ArrowRight, Globe, Sparkles, X } from 'lucide-react';

const KnowledgeUniversePitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const slides = [
    // Slide 0: Hero
    {
      id: 'hero',
      type: 'hero',
      content: {
        title: 'Knowledge Universe',
        subtitle: 'The Internet\'s Intelligence Layer',
        tagline: 'Real-time source discovery, validation, and serving API',
        cta: 'Watch Demo',
        bg: 'from-blue-900 via-purple-900 to-pink-900'
      }
    },
    // Slide 1: The Problem
    {
      id: 'problem',
      type: 'problem',
      content: {
        title: 'The $10B Problem',
        subtitle: 'AI applications need quality sources, not hallucinations',
        problems: [
          { title: 'LLMs Hallucinate', desc: '30% of AI responses contain false information', icon: '🤥', impact: '$10B lost annually' },
          { title: 'Manual Curation is Expensive', desc: 'Companies spend $50K-500K/year curating sources', icon: '💸', impact: '200+ hours/month' },
          { title: 'Static Data Gets Stale', desc: 'Traditional databases update weekly, miss breaking changes', icon: '📚', impact: '70% outdated within 30 days' },
          { title: 'No Quality Validation', desc: 'Search engines return spam, low-quality content', icon: '🗑️', impact: '40% irrelevant results' }
        ]
      }
    },
    // Slide 2: The Solution
    {
      id: 'solution',
      type: 'solution',
      content: {
        title: 'Our Solution',
        subtitle: 'Real-time, validated sources on-demand',
        features: [
          { icon: Zap, title: 'Real-time Discovery', desc: '6+ agents search simultaneously in <2 seconds', color: 'from-yellow-500 to-orange-500' },
          { icon: CheckCircle, title: 'AI Quality Scoring', desc: '6-factor validation: authority, freshness, depth, readability', color: 'from-green-500 to-emerald-500' },
          { icon: Code, title: 'Format Agnostic', desc: 'CSV, JSON, XML, Parquet, Excel - your choice', color: 'from-blue-500 to-cyan-500' },
          { icon: Shield, title: 'Zero Storage', desc: 'No database = always fresh, ultra-low cost', color: 'from-purple-500 to-pink-500' }
        ]
      }
    },
    // Slide 3: How It Works
    {
      id: 'how',
      type: 'how',
      content: {
        title: 'How It Works',
        subtitle: 'From query to validated sources in 1.8 seconds',
        steps: [
          { num: 1, title: 'Query In', desc: 'Your app sends: "React Hooks tutorial, intermediate level"', time: '0ms' },
          { num: 2, title: 'Parallel Discovery', desc: '6 agents search Google, GitHub, Stack Overflow, arXiv, YouTube, Medium', time: '500-1500ms' },
          { num: 3, title: 'AI Quality Scoring', desc: 'Each source scored on 6 dimensions in real-time', time: '200-500ms' },
          { num: 4, title: 'Format & Return', desc: 'Delivered in your preferred format with metadata', time: '50ms' }
        ]
      }
    },
    // Slide 4: Demo Video
    {
      id: 'demo',
      type: 'demo',
      content: {
        title: 'See It In Action',
        subtitle: 'Live demo of the API in production',
        videoId: '1s3CgcqMrjRv15foPZBjaqc2m-ZC-LTK4'
      }
    },
    // Slide 5: Use Cases
    {
      id: 'usecases',
      type: 'usecases',
      content: {
        title: 'Who Needs This?',
        subtitle: 'Powering the next generation of intelligent applications',
        cases: [
          { icon: '🤖', title: 'AI/LLM Companies', desc: 'RAG systems, chatbots, research assistants', companies: 'OpenAI, Anthropic, Perplexity', value: '$500-5K/mo' },
          { icon: '🎓', title: 'EdTech Platforms', desc: 'Personalized learning, course recommendations', companies: 'Coursera, Udemy, Khan Academy', value: '$1K-10K/mo' },
          { icon: '💻', title: 'Developer Tools', desc: 'IDE plugins, documentation, code review', companies: 'GitHub Copilot, Cursor, Replit', value: '$2K-20K/mo' },
          { icon: '🏢', title: 'Enterprise', desc: 'Internal knowledge management, compliance', companies: 'Fortune 500 companies', value: '$10K-100K/mo' }
        ]
      }
    },
    // Slide 6: Pricing
    {
      id: 'pricing',
      type: 'pricing',
      content: {
        title: 'Simple, Transparent Pricing',
        subtitle: 'Start free, scale as you grow',
        tiers: [
          {
            name: 'Free',
            price: '$0',
            period: 'forever',
            requests: '1,000/month',
            features: ['Basic sources (3)', 'JSON output', '24hr cache', 'Community support'],
            cta: 'Start Free',
            popular: false,
            color: 'from-gray-600 to-gray-700'
          },
          {
            name: 'Starter',
            price: '$29',
            period: 'per month',
            requests: '10,000/month',
            features: ['All 6+ sources', 'CSV/JSON/XML output', '1hr cache', 'Email support', 'Analytics dashboard'],
            cta: 'Start Trial',
            popular: false,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            name: 'Pro',
            price: '$99',
            period: 'per month',
            requests: '100,000/month',
            features: ['Priority queue', 'Real-time validation', 'All formats (CSV, JSON, XML, Parquet, Excel)', 'Webhooks', 'Slack support', 'Custom rate limits'],
            cta: 'Start Trial',
            popular: true,
            color: 'from-green-500 to-emerald-500'
          },
          {
            name: 'Enterprise',
            price: 'Custom',
            period: 'contact sales',
            requests: 'Unlimited',
            features: ['Custom agents', 'White-label option', 'SLA 99.9%', 'Dedicated support', 'On-premise deployment', 'Custom integrations'],
            cta: 'Contact Sales',
            popular: false,
            color: 'from-purple-500 to-pink-500'
          }
        ]
      }
    },
    // Slide 7: ROI Calculator
    {
      id: 'roi',
      type: 'roi',
      content: {
        title: 'Calculate Your ROI',
        subtitle: 'See how much you\'ll save',
        scenarios: [
          {
            title: 'Building In-House',
            costs: [
              { item: '2 Engineers (6 months)', cost: 120000 },
              { item: 'Infrastructure', cost: 24000 },
              { item: 'Maintenance (yearly)', cost: 50000 }
            ],
            total: 194000,
            time: '6 months to build'
          },
          {
            title: 'Manual Curation',
            costs: [
              { item: '1 Content Curator (FTE)', cost: 80000 },
              { item: 'Research Tools', cost: 12000 },
              { item: 'Quality Issues', cost: 30000 }
            ],
            total: 122000,
            time: 'Ongoing, never done'
          },
          {
            title: 'Knowledge Universe',
            costs: [
              { item: 'Pro Plan (yearly)', cost: 1188 },
              { item: 'Integration (1 day)', cost: 1000 },
              { item: 'Maintenance', cost: 0 }
            ],
            total: 2188,
            time: '1 day integration'
          }
        ]
      }
    },
    // Slide 8: Traction
    {
      id: 'traction',
      type: 'traction',
      content: {
        title: 'Early Traction',
        subtitle: 'Built in public, growing fast',
        metrics: [
          { value: '50+', label: 'Beta Users', change: '+120% MoM', icon: Users },
          { value: '500K+', label: 'API Calls', change: '+200% MoM', icon: Zap },
          { value: '99.8%', label: 'Uptime', change: 'SLA compliant', icon: CheckCircle },
          { value: '$5K', label: 'MRR', change: '+300% MoM', icon: DollarSign }
        ],
        testimonials: [
          { text: 'Cut our source curation time by 90%. Game changer.', author: 'CTO, AI Startup', company: 'Series A Company' },
          { text: 'Better results than building in-house. 10x faster.', author: 'Engineering Lead', company: 'EdTech Platform' }
        ]
      }
    },
    // Slide 9: Why Now
    {
      id: 'why',
      type: 'why',
      content: {
        title: 'Why Now?',
        subtitle: 'Perfect timing for explosive growth',
        reasons: [
          { icon: '🚀', title: 'AI Boom', desc: 'Every company building AI apps - 300% YoY growth in AI startups', stat: '$200B market by 2026' },
          { icon: '🔥', title: 'RAG Revolution', desc: 'Retrieval-Augmented Generation is the new standard for LLMs', stat: '85% of LLM apps use RAG' },
          { icon: '💰', title: 'API Economy', desc: 'Developer tools market growing 25% annually', stat: '$12.5B TAM' },
          { icon: '⚡', title: 'No Competition', desc: 'No direct competitor doing real-time discovery + validation', stat: 'First mover advantage' }
        ]
      }
    },
    // Slide 10: Team & Contact
    {
      id: 'team',
      type: 'team',
      content: {
        title: 'Let\'s Build Together',
        subtitle: 'Ready to power your intelligent applications',
        cta: {
          primary: 'Start Free Trial',
          secondary: 'Schedule Demo',
          email: 'hello@knowledgeuniverse.io',
          docs: 'docs.knowledgeuniverse.io'
        },
        final: 'Join 50+ companies building the future of AI-powered applications'
      }
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-gray-950 text-white relative overflow-hidden">
      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full max-w-6xl">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full rounded-lg"
                src="https://drive.google.com/file/d/1s3CgcqMrjRv15foPZBjaqc2m-ZC-LTK4/preview"
                allow="autoplay"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Progress Bar */}
        <div className="fixed top-0 left-0 right-0 h-1 bg-gray-800 z-50">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>

        {/* Slide Content */}
        <div className="flex-1 flex items-center justify-center p-8 pt-16">
          {/* Hero Slide */}
          {currentSlideData.type === 'hero' && (
            <div className="max-w-6xl mx-auto text-center">
              <div className="mb-8">
                <Globe className="w-24 h-24 mx-auto mb-6 text-cyan-400 animate-pulse" />
                <h1 className="text-7xl font-bold mb-4 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {currentSlideData.content.title}
                </h1>
                <p className="text-3xl text-gray-300 mb-3">{currentSlideData.content.subtitle}</p>
                <p className="text-xl text-gray-400">{currentSlideData.content.tagline}</p>
              </div>
              <button
                onClick={() => setShowVideo(true)}
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-500 px-8 py-4 rounded-full text-xl font-semibold hover:scale-105 transition-transform shadow-lg shadow-blue-500/50"
              >
                <Play className="w-6 h-6" />
                {currentSlideData.content.cta}
                <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              </button>
            </div>
          )}

          {/* Problem Slide */}
          {currentSlideData.type === 'problem' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {currentSlideData.content.problems.map((problem, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-red-500/20 hover:border-red-500/40 transition-all">
                    <div className="text-6xl mb-4">{problem.icon}</div>
                    <h3 className="text-2xl font-bold mb-3 text-red-400">{problem.title}</h3>
                    <p className="text-gray-300 mb-4">{problem.desc}</p>
                    <div className="text-xl font-semibold text-orange-400">{problem.impact}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Solution Slide */}
          {currentSlideData.type === 'solution' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {currentSlideData.content.features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-green-500/20 hover:border-green-500/40 transition-all">
                      <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                      <p className="text-gray-300">{feature.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* How It Works Slide */}
          {currentSlideData.type === 'how' && (
            <div className="max-w-5xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="space-y-6">
                {currentSlideData.content.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-2xl font-bold">
                      {step.num}
                    </div>
                    <div className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 border border-cyan-500/20">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-2xl font-bold">{step.title}</h3>
                        <span className="text-sm bg-cyan-900/50 px-3 py-1 rounded-full text-cyan-300">{step.time}</span>
                      </div>
                      <p className="text-gray-300">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 px-6 py-3 rounded-full">
                  <Zap className="w-5 h-5 text-green-400" />
                  <span className="text-xl font-semibold">Total Time: <span className="text-green-400">1.8 seconds</span></span>
                </div>
              </div>
            </div>
          )}

          {/* Demo Video Slide */}
          {currentSlideData.type === 'demo' && (
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-6xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-8">{currentSlideData.content.subtitle}</p>
              <div
                className="relative cursor-pointer group rounded-2xl overflow-hidden"
                onClick={() => setShowVideo(true)}
                style={{ paddingBottom: '56.25%' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                  <div className="text-center">
                    <Play className="w-24 h-24 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <p className="text-2xl font-semibold">Click to Watch Demo</p>
                    <p className="text-gray-200 mt-2">See Knowledge Universe in action</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Use Cases Slide */}
          {currentSlideData.type === 'usecases' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {currentSlideData.content.cases.map((useCase, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-yellow-500/20 hover:border-yellow-500/40 transition-all">
                    <div className="text-5xl mb-4">{useCase.icon}</div>
                    <h3 className="text-2xl font-bold mb-2">{useCase.title}</h3>
                    <p className="text-gray-300 mb-4">{useCase.desc}</p>
                    <div className="text-sm text-gray-400 mb-3">Example: {useCase.companies}</div>
                    <div className="text-xl font-bold text-green-400">{useCase.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Slide */}
          {currentSlideData.type === 'pricing' && (
            <div className="max-w-7xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid md:grid-cols-4 gap-6">
                {currentSlideData.content.tiers.map((tier, idx) => (
                  <div
                    key={idx}
                    className={`relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border transition-all cursor-pointer ${
                      tier.popular
                        ? 'border-green-500 shadow-lg shadow-green-500/20 scale-105'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedTier(idx)}
                  >
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-1 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </div>
                    )}
                    <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold">{tier.price}</span>
                      <span className="text-gray-400 text-sm ml-2">{tier.period}</span>
                    </div>
                    <div className="text-sm text-gray-400 mb-6">{tier.requests}</div>
                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button className={`w-full py-3 rounded-lg font-semibold transition-all ${
                      tier.popular
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:scale-105'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}>
                      {tier.cta}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROI Slide */}
          {currentSlideData.type === 'roi' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid md:grid-cols-3 gap-6">
                {currentSlideData.content.scenarios.map((scenario, idx) => (
                  <div key={idx} className={`rounded-2xl p-8 ${
                    idx === 2
                      ? 'bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-2 border-green-500 scale-105'
                      : 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700'
                  }`}>
                    <h3 className="text-2xl font-bold mb-6">{scenario.title}</h3>
                    <div className="space-y-3 mb-6">
                      {scenario.costs.map((cost, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-400">{cost.item}</span>
                          <span className="font-semibold">${cost.cost.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-gray-700 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Total Cost:</span>
                        <span className={`text-3xl font-bold ${idx === 2 ? 'text-green-400' : 'text-red-400'}`}>
                          ${scenario.total.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-400">{scenario.time}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 px-8 py-4 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  <span className="text-2xl font-bold">Save <span className="text-green-400">98.9%</span> vs Building In-House</span>
                </div>
              </div>
            </div>
          )}

          {/* Traction Slide */}
          {currentSlideData.type === 'traction' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">{currentSlideData.content.subtitle}</p>
              <div className="grid grid-cols-4 gap-6 mb-12">
                {currentSlideData.content.metrics.map((metric, idx) => {
                  const Icon = metric.icon;
                  return (
                    <div key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-cyan-500/20 text-center">
                      <Icon className="w-8 h-8 mx-auto mb-3 text-cyan-400" />
                      <div className="text-4xl font-bold mb-2">{metric.value}</div>
                                            <div className="text-gray-400 mb-1">{metric.label}</div>
                      <div className="text-sm text-green-400">{metric.change}</div>
                    </div>
                  );
                })}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {currentSlideData.content.testimonials.map((t, i) => (
                  <div key={i} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <p className="text-gray-300 italic mb-4">“{t.text}”</p>
                    <div className="text-sm text-gray-400">
                      — {t.author}, <span className="text-cyan-400">{t.company}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Why Now Slide */}
          {currentSlideData.type === 'why' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-6xl font-bold mb-4 text-center bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-12 text-center">
                {currentSlideData.content.subtitle}
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {currentSlideData.content.reasons.map((r, i) => (
                  <div key={i} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-purple-500/20">
                    <div className="text-5xl mb-4">{r.icon}</div>
                    <h3 className="text-2xl font-bold mb-2">{r.title}</h3>
                    <p className="text-gray-300 mb-3">{r.desc}</p>
                    <div className="text-lg font-bold text-purple-400">{r.stat}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Final CTA Slide */}
          {currentSlideData.type === 'team' && (
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                {currentSlideData.content.title}
              </h2>
              <p className="text-2xl text-gray-300 mb-10">
                {currentSlideData.content.subtitle}
              </p>

              <div className="flex flex-col md:flex-row gap-6 justify-center mb-10">
              {/* Start Free Trial → Coming Soon */}
              <button
                onClick={() => alert("🚧 Our Servers are getting too much request in limited time!! please click Schedule Demo")}
                className="px-10 py-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-xl font-bold hover:scale-105 transition"
              >
                Start Free Trial
              </button>

              {/* Schedule Demo → LinkedIn */}
              <a
                href="https://www.linkedin.com/in/v-l-siddarth-2147b9250/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 rounded-full bg-gray-800 border border-gray-700 text-xl font-bold hover:bg-gray-700 transition text-center"
              >
                Schedule Demo
              </a>

            </div>


              <div className="text-gray-400 space-y-2">
                <div>📧 vlsiddarth7@gmail.com</div>
                <div>Ask us on Linkendin (Limited Offers only)</div>
              </div>

              <p className="mt-10 text-xl text-cyan-400 font-semibold">
                {currentSlideData.content.final}
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center p-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-800 disabled:opacity-30"
          >
            ← Back
          </button>

          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-3 h-3 rounded-full ${
                  i === currentSlide ? 'bg-cyan-400' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeUniversePitchDeck;
